import { useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import {
  useBusinessAnnouncementDetail,
  useBusinessAnnouncementMarkAsRead,
  type AnnouncementItem,
} from "~/services/business/announcement";
import { sanitizeAnnouncementHtml } from "%/sanitizeHtml";

interface SearchParams {
  id: number;
}

export const Route = createFileRoute(
  "/business/layout/user-owner-announcement-detail"
)({
  component: BusinessAnnouncementDetail,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    id: Number(search.id),
  }),
});

const CATEGORY_LABEL: Record<AnnouncementItem["category"], string> = {
  SYSTEM: "업데이트",
  MAINTENANCE: "점검",
  EVENT: "이벤트",
  GENERAL: "공지",
};

const CATEGORY_TONE: Record<
  AnnouncementItem["category"],
  { bg: string; text: string }
> = {
  SYSTEM: { bg: "bg-[#DBEAFE]", text: "text-[#1D4ED8]" },
  MAINTENANCE: { bg: "bg-amber-100", text: "text-amber-700" },
  EVENT: { bg: "bg-green-100", text: "text-green-700" },
  GENERAL: { bg: "bg-[#F3F4F6]", text: "text-[#374151]" },
};

function BusinessAnnouncementDetail() {
  const { id } = Route.useSearch();
  const { data, isLoading } = useBusinessAnnouncementDetail(id);
  const markAsRead = useBusinessAnnouncementMarkAsRead();

  const safeHtml = useMemo(
    () => sanitizeAnnouncementHtml(data?.contentHtml ?? ""),
    [data?.contentHtml]
  );

  useEffect(() => {
    if (data?.id) markAsRead.mutate(data.id);
    // markAsRead는 stable한 mutation 객체 가정. data.id만 의존.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  return (
    <>
      <LayoutHeader title={"공지사항"} hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5] px-3 pt-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-40 text-sm text-[#666666]">
            로딩 중...
          </div>
        ) : !data ? (
          <div className="flex items-center justify-center h-40 text-sm text-[#666666]">
            공지사항을 찾을 수 없습니다.
          </div>
        ) : (
          <div className="bg-white border-2 border-[#E9E9E9] rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {data.isPinned && (
                <span className="inline-flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-[11px] font-semibold text-orange-600">
                  고정
                </span>
              )}
              {data.isImportant && (
                <span className="inline-flex items-center rounded bg-[#FEE2E2] px-1.5 py-0.5 text-[11px] font-semibold text-[#DC2626]">
                  중요
                </span>
              )}
              {(() => {
                const tone = CATEGORY_TONE[data.category];
                return (
                  <span
                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold ${tone.bg} ${tone.text}`}
                  >
                    {CATEGORY_LABEL[data.category]}
                  </span>
                );
              })()}
            </div>
            <h2 className="text-[17px] font-bold mb-1">{data.title}</h2>
            <p className="text-xs text-[#999999] mb-4">{data.createdAt}</p>
            <div
              className="announcement-rich-content"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </div>
        )}
      </div>
    </>
  );
}
